/* Ulif Customer Reviews v4: direct Judge.me JSON carousel with no proxy or private token. */
(function registerUlifCustomerReviewsV4(global) {
  'use strict';

  const ELEMENT_NAME = 'ulif-customer-reviews-v4';
  const SECTION_LOAD_FLAG = '__ulifCustomerReviewsV4SectionLoadBound';
  const JUDGEME_CAROUSEL_ENDPOINT = 'https://cdn.judge.me/reviews/reviews_for_carousel';
  const SESSION_CACHE_PREFIX = 'ulif-customer-reviews-v4';
  const SESSION_CACHE_TTL_MS = 10 * 60 * 1000;
  const sharedRequests = global.__ulifJudgeMeCarouselRequests || new Map();
  global.__ulifJudgeMeCarouselRequests = sharedRequests;

  function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim().length > 0;
  }

  function hasBrokenEncoding(value) {
    return typeof value === 'string' && value.includes('\uFFFD');
  }

  function clampInteger(value, minimum, maximum, fallback) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(maximum, Math.max(minimum, parsed));
  }

  function isValidShopDomain(value) {
    return isNonEmptyString(value) && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(value.trim());
  }

  function isSafeStorefrontUrl(value) {
    if (!isNonEmptyString(value)) return false;
    try {
      return new URL(value).protocol === 'https:';
    } catch (_error) {
      return false;
    }
  }

  function isSafeProductPath(value) {
    if (!isNonEmptyString(value)) return false;
    const path = value.trim();
    return /^\/products\/[a-z0-9][a-z0-9-]*(?:[?#].*)?$/i.test(path)
      && !/[\u0000-\u001F\u007F]/.test(path);
  }

  function isSafeShopifyImageUrl(value) {
    if (!isNonEmptyString(value)) return false;
    try {
      const url = new URL(value.trim());
      return url.protocol === 'https:'
        && (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com'));
    } catch (_error) {
      return false;
    }
  }

  function optimizeShopifyImageUrl(value) {
    if (!isSafeShopifyImageUrl(value)) return null;
    const url = new URL(value.trim());
    url.searchParams.set('width', '112');
    return url.toString();
  }

  function decodeBasicHtmlEntities(value) {
    if (!isNonEmptyString(value)) return null;
    return value.trim()
      .replace(/&quot;/gi, '"')
      .replace(/&#0*39;|&#x0*27;/gi, "'")
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>');
  }

  function productHandleFromPath(path) {
    if (!isSafeProductPath(path)) return null;
    return path.trim().split('/products/')[1].split(/[?#]/)[0] || null;
  }

  function buildJudgeMeCarouselUrl(config = {}) {
    const shopDomain = isNonEmptyString(config.shopDomain) ? config.shopDomain.trim() : '';
    const shopUrl = isNonEmptyString(config.shopUrl) ? config.shopUrl.trim() : '';
    if (!isValidShopDomain(shopDomain)) throw new Error('INVALID_SHOP_DOMAIN');
    if (!isSafeStorefrontUrl(shopUrl)) throw new Error('INVALID_SHOP_URL');

    const languageCandidate = isNonEmptyString(config.language) ? config.language.trim().toLowerCase() : 'en';
    const language = /^[a-z]{2}$/.test(languageCandidate) ? languageCandidate : 'en';
    const maximumReviews = clampInteger(config.maximumReviews, 1, 20, 20);
    const url = new URL(JUDGEME_CAROUSEL_ENDPOINT);
    url.searchParams.set('reviews_selection', 'all');
    url.searchParams.set('carousel_type', 'testimonials');
    url.searchParams.set('star_rating', 'all');
    url.searchParams.set('max_reviews', String(maximumReviews));
    url.searchParams.set('url', shopUrl);
    url.searchParams.set('shop_domain', shopDomain);
    url.searchParams.set('platform', 'shopify');
    url.searchParams.set('primary_language', language);
    return url;
  }

  function normalizeJudgeMeReview(review, options = {}) {
    if (!review || !isNonEmptyString(review.uuid) || !isNonEmptyString(review.body)) return null;
    if (!isNonEmptyString(review.reviewer_name) || !isNonEmptyString(review.created_at)) return null;

    const rating = Number(review.rating);
    if (!Number.isFinite(rating) || rating < 1 || rating > 5) return null;

    const title = decodeBasicHtmlEntities(review.title);
    const body = review.body.trim();
    const author = review.reviewer_name.trim();
    if (options.excludeBrokenEncoding !== false && [title, body, author].some(hasBrokenEncoding)) return null;
    if (options.onlyCurrentProducts !== false && review.product_in_store !== true) return null;

    const productUrl = review.product_in_store === true && isSafeProductPath(review.product_url)
      ? review.product_url.trim()
      : null;
    const product = productUrl
      ? {
          title: decodeBasicHtmlEntities(review.product_title),
          handle: productHandleFromPath(productUrl),
          url: productUrl,
          imageUrl: optimizeShopifyImageUrl(review.product_variant_image_url),
        }
      : null;

    const badges = Array.isArray(review.transparency_badges) ? review.transparency_badges : [];
    const imported = badges.includes('review_collected_from_another_provider');
    return {
      id: review.uuid.trim(),
      title,
      body,
      rating,
      author,
      date: review.created_at.trim(),
      verified: review.verified_buyer === true,
      product,
      sourceLabel: imported && options.showImportedLabel === true && isNonEmptyString(options.importedLabel)
        ? options.importedLabel.trim()
        : null,
    };
  }

  function normalizeJudgeMePayload(payload, options = {}) {
    const sourceReviews = Array.isArray(payload?.reviews) ? payload.reviews : [];
    const uniqueIds = new Set();
    const limit = clampInteger(options.limit, 1, 20, 8);
    const reviews = sourceReviews
      .map((review) => normalizeJudgeMeReview(review, options))
      .filter((review) => {
        if (!review || uniqueIds.has(review.id)) return false;
        uniqueIds.add(review.id);
        return true;
      })
      .slice(0, limit);

    return { reviews, fetchedCount: sourceReviews.length };
  }

  function nextIndex(index, length, delta) {
    if (!length) return 0;
    return (index + delta + length) % length;
  }

  function getLoopTarget(activeIndex, length, cloneCount, delta) {
    if (!length) return { logicalIndex: 0, physicalIndex: 0, wraps: false };
    if (length === 1) return { logicalIndex: 0, physicalIndex: 0, wraps: false };
    const logicalIndex = nextIndex(activeIndex, length, delta);
    const safeCloneCount = Math.min(Math.max(0, cloneCount), length);
    if (delta > 0 && activeIndex === length - 1) {
      return { logicalIndex, physicalIndex: safeCloneCount + length, wraps: true };
    }
    if (delta < 0 && activeIndex === 0) {
      return { logicalIndex, physicalIndex: Math.max(0, safeCloneCount - 1), wraps: true };
    }
    return { logicalIndex, physicalIndex: safeCloneCount + logicalIndex, wraps: false };
  }

  function readSessionCache(key) {
    try {
      const cached = JSON.parse(global.sessionStorage?.getItem(key) || 'null');
      if (!cached || cached.expiresAt <= Date.now() || !cached.payload) return null;
      return cached.payload;
    } catch (_error) {
      return null;
    }
  }

  function writeSessionCache(key, payload) {
    try {
      global.sessionStorage?.setItem(key, JSON.stringify({
        expiresAt: Date.now() + SESSION_CACHE_TTL_MS,
        payload,
      }));
    } catch (_error) {
      // The carousel still works when storage is disabled or full.
    }
  }

  function requestJudgeMePayload(url, timeoutMs) {
    const key = url.toString();
    if (sharedRequests.has(key)) return sharedRequests.get(key);

    const request = (async () => {
      const controller = new AbortController();
      const timeout = global.setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await global.fetch(url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`JUDGEME_HTTP_${response.status}`);
        return response.json();
      } finally {
        global.clearTimeout(timeout);
      }
    })();

    sharedRequests.set(key, request);
    request.then(
      () => sharedRequests.delete(key),
      () => sharedRequests.delete(key),
    );
    return request;
  }

  const testApi = {
    JUDGEME_CAROUSEL_ENDPOINT,
    buildJudgeMeCarouselUrl,
    getLoopTarget,
    isSafeProductPath,
    normalizeJudgeMePayload,
    normalizeJudgeMeReview,
    nextIndex,
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = testApi;
  global.UlifCustomerReviewsV4TestApi = testApi;

  if (!global.HTMLElement || !global.customElements || !global.document) return;

  class UlifCustomerReviewsV4 extends HTMLElement {
    constructor() {
      super();
      this.eventController = null;
      this.reviews = [];
      this.activeIndex = 0;
      this.activePhysicalIndex = 0;
      this.loopCloneCount = 0;
      this.loadToken = 0;
      this.scrollFrame = null;
      this.scrollSettleTimer = null;
      this.dragState = null;
      this.suppressClick = false;
      this.hasEmittedView = false;
    }

    connectedCallback() {
      this.eventController?.abort();
      this.eventController = new AbortController();
      const { signal } = this.eventController;
      this.addEventListener('click', (event) => this.handleClick(event), { signal });
      this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      const rail = this.querySelector('[data-review-rail]');
      rail?.addEventListener('scroll', () => this.handleScroll(), { passive: true, signal });
      rail?.addEventListener('scrollend', () => this.handleScrollEnd(), { signal });
      rail?.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
      rail?.addEventListener('pointermove', (event) => this.handlePointerMove(event), { signal });
      rail?.addEventListener('pointerup', (event) => this.handlePointerEnd(event), { signal });
      rail?.addEventListener('pointercancel', (event) => this.handlePointerEnd(event), { signal });
      this.loadReviews();
    }

    disconnectedCallback() {
      this.loadToken += 1;
      this.eventController?.abort();
      this.eventController = null;
      if (this.scrollFrame) global.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      if (this.scrollSettleTimer) global.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = null;
      this.dragState = null;
    }

    getOptions() {
      return {
        onlyCurrentProducts: this.dataset.onlyCurrentProducts !== 'false',
        excludeBrokenEncoding: true,
        showImportedLabel: this.dataset.showImportedLabel === 'true',
        importedLabel: this.dataset.importedLabel || 'Imported review',
        limit: clampInteger(this.dataset.displayLimit, 1, 12, 8),
      };
    }

    getRequestUrl() {
      return buildJudgeMeCarouselUrl({
        shopDomain: this.dataset.shopDomain,
        shopUrl: this.dataset.shopUrl,
        language: this.dataset.primaryLanguage,
        maximumReviews: this.dataset.requestLimit,
      });
    }

    getCacheKey(url) {
      return `${SESSION_CACHE_PREFIX}:${url.searchParams.get('shop_domain')}:${url.searchParams.get('primary_language')}:${url.searchParams.get('max_reviews')}`;
    }

    async loadReviews({ bypassCache = false } = {}) {
      const token = ++this.loadToken;
      this.setState('loading');

      let url;
      try {
        url = this.getRequestUrl();
      } catch (error) {
        this.setState('error');
        this.emit('ulif:review_error', { code: error?.message || 'INVALID_CONFIGURATION' });
        return;
      }

      try {
        const cacheKey = this.getCacheKey(url);
        let rawPayload = bypassCache ? null : readSessionCache(cacheKey);
        if (!rawPayload) {
          const timeoutMs = clampInteger(this.dataset.timeoutMs, 3000, 9000, 8000);
          rawPayload = await requestJudgeMePayload(url, timeoutMs);
          writeSessionCache(cacheKey, rawPayload);
        }
        if (token !== this.loadToken || !this.isConnected) return;
        this.applyPayload(rawPayload);
      } catch (error) {
        if (token !== this.loadToken || !this.isConnected) return;
        this.setState('error');
        this.emit('ulif:review_error', {
          code: error?.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK_ERROR',
        });
      }
    }

    applyPayload(rawPayload) {
      const normalized = normalizeJudgeMePayload(rawPayload, this.getOptions());
      this.reviews = normalized.reviews;
      this.activeIndex = 0;
      this.dataset.reviewFetchedCount = String(normalized.fetchedCount);
      this.dataset.reviewVisibleCount = String(this.reviews.length);
      if (!this.reviews.length) {
        this.setState('empty');
        return;
      }

      this.renderReviews();
      this.setState('success');
      this.updateControls();
      this.initializeRail();
      if (!this.hasEmittedView) {
        this.hasEmittedView = true;
        this.emit('ulif:review_view', { visibleCount: this.reviews.length });
      }
    }

    setState(state) {
      const content = this.querySelector('[data-review-state]');
      if (!content) return;
      content.dataset.reviewState = state;
      content.setAttribute('aria-busy', String(state === 'loading'));
      this.querySelector('[data-review-loading]')?.toggleAttribute('hidden', state !== 'loading');
      this.querySelector('[data-review-carousel]')?.toggleAttribute('hidden', state !== 'success');
      this.querySelector('[data-review-empty]')?.toggleAttribute('hidden', state !== 'empty');
      this.querySelector('[data-review-error]')?.toggleAttribute('hidden', state !== 'error');
    }

    createTextElement(tagName, className, text) {
      const element = global.document.createElement(tagName);
      if (className) element.className = className;
      element.textContent = text || '';
      return element;
    }

    renderReviews() {
      const rail = this.querySelector('[data-review-rail]');
      if (!rail) return;
      rail.replaceChildren();
      rail.dataset.reviewInitializing = '';

      this.loopCloneCount = this.reviews.length > 1 ? Math.min(5, this.reviews.length) : 0;
      this.activePhysicalIndex = this.loopCloneCount;
      const logicalIndexes = [];
      for (let index = this.reviews.length - this.loopCloneCount; index < this.reviews.length; index += 1) {
        logicalIndexes.push({ logicalIndex: index, clone: true });
      }
      this.reviews.forEach((_review, logicalIndex) => logicalIndexes.push({ logicalIndex, clone: false }));
      for (let index = 0; index < this.loopCloneCount; index += 1) {
        logicalIndexes.push({ logicalIndex: index, clone: true });
      }

      const fragment = global.document.createDocumentFragment();
      logicalIndexes.forEach(({ logicalIndex, clone }, physicalIndex) => {
        fragment.append(this.createReviewCard(this.reviews[logicalIndex], logicalIndex, physicalIndex, clone));
      });
      rail.append(fragment);
    }

    createReviewCard(review, logicalIndex, physicalIndex, clone) {
      const card = global.document.createElement('article');
      card.className = 'ulif-customer-reviews-v4__card';
      card.dataset.reviewCard = '';
      card.dataset.reviewIndex = String(logicalIndex);
      card.dataset.reviewPhysicalIndex = String(physicalIndex);
      card.setAttribute('aria-roledescription', 'slide');
      if (clone) {
        card.dataset.reviewClone = '';
        card.setAttribute('aria-hidden', 'true');
      } else {
        card.setAttribute('aria-label', `Customer review ${logicalIndex + 1} of ${this.reviews.length}`);
      }

      const topLine = global.document.createElement('div');
      topLine.className = 'ulif-customer-reviews-v4__card-topline';
      const stars = this.createTextElement('p', 'ulif-customer-reviews-v4__stars', `${'\u2605'.repeat(Math.round(review.rating))}${'\u2606'.repeat(5 - Math.round(review.rating))}`);
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      topLine.append(stars);
      if (review.verified) topLine.append(this.createTextElement('span', 'ulif-customer-reviews-v4__provenance', 'Verified buyer'));
      else if (review.sourceLabel) topLine.append(this.createTextElement('span', 'ulif-customer-reviews-v4__provenance', review.sourceLabel));

      const blockquote = global.document.createElement('blockquote');
      blockquote.append(this.createTextElement('p', '', review.body));

      const attribution = global.document.createElement('div');
      attribution.className = 'ulif-customer-reviews-v4__attribution';
      attribution.append(this.createTextElement('p', 'ulif-customer-reviews-v4__author', review.author));
      const time = this.createTextElement('time', '', this.formatDate(review.date));
      time.dateTime = review.date;
      attribution.append(time);

      card.append(topLine);
      if (review.title) card.append(this.createTextElement('h3', 'ulif-customer-reviews-v4__card-title', review.title));
      card.append(blockquote, attribution);
      if (review.product?.url && review.product?.title) {
        const productLink = this.createProductLink(review);
        if (clone) productLink.tabIndex = -1;
        card.append(productLink);
      }
      return card;
    }

    initializeRail() {
      const rail = this.querySelector('[data-review-rail]');
      if (!rail) return;
      global.requestAnimationFrame(() => {
        this.scrollToPhysical(this.loopCloneCount, 'auto');
        global.requestAnimationFrame(() => rail.removeAttribute('data-review-initializing'));
      });
    }

    createProductLink(review) {
      const link = global.document.createElement('a');
      link.className = 'ulif-customer-reviews-v4__product';
      link.dataset.reviewProduct = '';
      link.href = review.product.url;
      if (review.product.imageUrl) {
        const image = global.document.createElement('img');
        image.src = review.product.imageUrl;
        image.alt = '';
        image.width = 56;
        image.height = 56;
        image.loading = 'lazy';
        image.decoding = 'async';
        link.append(image);
      }
      const copy = global.document.createElement('span');
      copy.className = 'ulif-customer-reviews-v4__product-copy';
      copy.append(
        this.createTextElement('small', '', 'Reviewed product'),
        this.createTextElement('span', '', review.product.title),
      );
      link.append(copy, this.createTextElement('span', 'ulif-customer-reviews-v4__product-arrow', '\u2192'));
      return link;
    }

    formatDate(value) {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return '';
      return new Intl.DateTimeFormat(global.document.documentElement.lang || 'en', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(parsed);
    }

    handleClick(event) {
      if (this.suppressClick) {
        event.preventDefault();
        this.suppressClick = false;
        return;
      }
      const retry = event.target.closest('[data-review-retry]');
      if (retry && this.contains(retry)) return this.loadReviews({ bypassCache: true });
      const previous = event.target.closest('[data-review-prev]');
      if (previous && this.contains(previous)) return this.move(-1);
      const next = event.target.closest('[data-review-next]');
      if (next && this.contains(next)) return this.move(1);
      const product = event.target.closest('[data-review-product]');
      if (product && this.contains(product)) this.emit('ulif:review_product_click');
      const all = event.target.closest('[data-review-all]');
      if (all && this.contains(all)) this.emit('ulif:review_all_click');
    }

    handleKeydown(event) {
      const rail = event.target.closest('[data-review-rail]');
      if (!rail || !this.contains(rail) || !this.reviews.length) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.move(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.move(1); }
      if (event.key === 'Home') { event.preventDefault(); this.goTo(0); }
      if (event.key === 'End') { event.preventDefault(); this.goTo(this.reviews.length - 1); }
    }

    move(delta) {
      if (!this.reviews.length) return;
      const target = getLoopTarget(this.activeIndex, this.reviews.length, this.loopCloneCount, delta);
      this.activeIndex = target.logicalIndex;
      this.activePhysicalIndex = target.physicalIndex;
      this.scrollToPhysical(target.physicalIndex, this.getScrollBehavior());
      this.updateControls();
      this.emit(delta < 0 ? 'ulif:review_previous' : 'ulif:review_next', { direction: delta });
    }

    goTo(index) {
      if (!this.reviews.length) return;
      this.activeIndex = Math.min(this.reviews.length - 1, Math.max(0, index));
      this.activePhysicalIndex = this.loopCloneCount + this.activeIndex;
      this.scrollToPhysical(this.activePhysicalIndex, this.getScrollBehavior());
      this.updateControls();
    }

    getScrollBehavior() {
      return global.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    getCardScrollLeft(rail, card) {
      const railRect = rail.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + (cardRect.width / 2);
      const railCenter = railRect.left + (rail.clientWidth / 2);
      return Math.max(0, rail.scrollLeft + cardCenter - railCenter);
    }

    scrollToPhysical(physicalIndex, behavior = 'smooth') {
      const rail = this.querySelector('[data-review-rail]');
      const card = rail?.querySelector(`[data-review-physical-index="${physicalIndex}"]`);
      if (card && rail) {
        rail.scrollTo({
          left: this.getCardScrollLeft(rail, card),
          behavior,
        });
      }
    }

    getNearestCard() {
      const rail = this.querySelector('[data-review-rail]');
      if (!rail) return null;
      const railRect = rail.getBoundingClientRect();
      const anchor = railRect.left + (rail.clientWidth / 2);
      let nearest = null;
      let nearestDistance = Number.POSITIVE_INFINITY;
      rail.querySelectorAll('[data-review-physical-index]').forEach((card) => {
        const cardRect = card.getBoundingClientRect();
        const distance = Math.abs(cardRect.left + (cardRect.width / 2) - anchor);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = card;
        }
      });
      return nearest;
    }

    handleScroll() {
      this.queueScrollState();
      if (this.scrollSettleTimer) global.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = global.setTimeout(() => this.handleScrollEnd(), 140);
    }

    queueScrollState() {
      if (this.scrollFrame) return;
      this.scrollFrame = global.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const nearest = this.getNearestCard();
        if (!nearest) return;
        const logicalIndex = Number(nearest.dataset.reviewIndex) || 0;
        const physicalIndex = Number(nearest.dataset.reviewPhysicalIndex) || 0;
        if (logicalIndex !== this.activeIndex || physicalIndex !== this.activePhysicalIndex) {
          this.activeIndex = logicalIndex;
          this.activePhysicalIndex = physicalIndex;
          this.updateControls();
        }
      });
    }

    handleScrollEnd() {
      if (this.scrollSettleTimer) global.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = null;
      if (this.dragState) return;
      const nearest = this.getNearestCard();
      if (!nearest) return;
      this.activeIndex = Number(nearest.dataset.reviewIndex) || 0;
      this.activePhysicalIndex = Number(nearest.dataset.reviewPhysicalIndex) || 0;
      this.updateControls();
      if (nearest.hasAttribute('data-review-clone')) {
        this.activePhysicalIndex = this.loopCloneCount + this.activeIndex;
        this.scrollToPhysical(this.activePhysicalIndex, 'auto');
        this.updateControls();
      }
    }

    handlePointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary) return;
      if (event.target.closest('a, button, input, textarea, select')) return;
      const rail = event.currentTarget;
      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: rail.scrollLeft,
        moved: false,
      };
      rail.setPointerCapture(event.pointerId);
    }

    handlePointerMove(event) {
      const rail = event.currentTarget;
      if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;
      const delta = event.clientX - this.dragState.startX;
      if (!this.dragState.moved && Math.abs(delta) < 5) return;
      this.dragState.moved = true;
      rail.dataset.reviewDragging = '';
      rail.scrollLeft = this.dragState.startScrollLeft - delta;
      event.preventDefault();
    }

    handlePointerEnd(event) {
      const rail = event.currentTarget;
      if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;
      const moved = this.dragState.moved;
      this.dragState = null;
      rail.removeAttribute('data-review-dragging');
      if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
      if (!moved) return;
      this.suppressClick = true;
      global.setTimeout(() => { this.suppressClick = false; }, 0);
      const nearest = this.getNearestCard();
      if (nearest) this.scrollToPhysical(Number(nearest.dataset.reviewPhysicalIndex), this.getScrollBehavior());
    }

    updateControls() {
      this.querySelectorAll('[data-review-prev], [data-review-next]').forEach((button) => {
        button.disabled = this.reviews.length <= 1;
      });
      this.querySelectorAll('[data-review-card]').forEach((card) => {
        const isActive = Number(card.dataset.reviewPhysicalIndex) === this.activePhysicalIndex;
        const isActiveOriginal = isActive && !card.hasAttribute('data-review-clone');
        card.toggleAttribute('data-review-active', isActive);
        if (isActiveOriginal) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
      });
    }

    emit(eventName, extraDetail = {}) {
      const review = this.reviews[this.activeIndex] || null;
      this.dispatchEvent(new CustomEvent(eventName, {
        bubbles: true,
        composed: true,
        detail: {
          sectionId: this.dataset.sectionId || null,
          reviewId: review?.id || null,
          productHandle: review?.product?.handle || null,
          ...extraDetail,
        },
      }));
    }

    reinitialize() {
      this.connectedCallback();
    }
  }

  if (!global.customElements.get(ELEMENT_NAME)) global.customElements.define(ELEMENT_NAME, UlifCustomerReviewsV4);

  if (!global[SECTION_LOAD_FLAG]) {
    global[SECTION_LOAD_FLAG] = true;
    global.document.addEventListener('shopify:section:load', (event) => {
      const target = event.target;
      if (!(target instanceof global.Element)) return;
      const sections = [];
      if (target.matches(ELEMENT_NAME)) sections.push(target);
      sections.push(...target.querySelectorAll(ELEMENT_NAME));
      sections.forEach((section) => section.reinitialize());
    });
  }
})(globalThis);
